/**
 * Admin Email Utility Functions
 * Provides secure storage and retrieval of admin email using Base64 encoding
 */

// Simple key for encoding/decoding (in production, use environment variable)
const ENCODE_KEY = 'cc_admin_email_key_2025';

/**
 * Encode admin email using Base64 encoding
 * @param {string} adminEmail - The admin email to encode
 * @returns {string} - Encoded admin email
 */
const encodeAdminEmail = (adminEmail) => {
  if (!adminEmail) return '';
  
  try {
    // Combine admin email with key and encode
    const combined = `${adminEmail}:${ENCODE_KEY}`;
    return btoa(combined);
  } catch (error) {
    console.error('Error encoding admin email:', error);
    return '';
  }
};

/**
 * Decode admin email from Base64 encoding
 * @param {string} encodedAdminEmail - The encoded admin email
 * @returns {string|null} - Decoded admin email or null if invalid
 */
const decodeAdminEmail = (encodedAdminEmail) => {
  if (!encodedAdminEmail) return null;
  
  try {
    const decoded = atob(encodedAdminEmail);
    const parts = decoded.split(':');
    
    // Verify the key matches
    if (parts.length === 2 && parts[1] === ENCODE_KEY) {
      return parts[0];
    }
    
    return null;
  } catch (error) {
    console.error('Error decoding admin email:', error);
    return null;
  }
};

/**
 * Store admin email securely in storage
 * @param {string} adminEmail - The admin email to store
 * @param {boolean} remember - Whether to use localStorage (true) or sessionStorage (false)
 */
export const setAdminEmail = (adminEmail, remember = false) => {
  const encodedEmail = encodeAdminEmail(adminEmail);
  
  if (remember) {
    localStorage.setItem('cc_ae', encodedEmail);
  } else {
    sessionStorage.setItem('cc_ae', encodedEmail);
  }
};

/**
 * Get admin email from storage and decode it
 * @returns {string|null} - The decoded admin email or null if not found/invalid
 */
export const getAdminEmail = () => {
  // Try to get from sessionStorage first, then localStorage
  const encodedEmail = sessionStorage.getItem('cc_ae') || localStorage.getItem('cc_ae');
  
  if (!encodedEmail) {
    return null;
  }
  
  return decodeAdminEmail(encodedEmail);
};

/**
 * Remove admin email from storage
 */
export const clearAdminEmail = () => {
  sessionStorage.removeItem('cc_ae');
  localStorage.removeItem('cc_ae');
  
  // Also clear the old 'cc-email' keys for backward compatibility
  sessionStorage.removeItem('cc-email');
  localStorage.removeItem('cc-email');
};

/**
 * Check if admin email exists in storage
 * @returns {boolean} - True if admin email is stored
 */
export const hasAdminEmail = () => {
  const adminEmail = getAdminEmail();
  return adminEmail !== null && adminEmail !== '';
};

/**
 * Validate email format
 * @param {string} email - The email to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};